import {
  useBillData,
  useBillGenerationSummary,
  useDownloadBill,
  useUtilityServices,
  useViewBill,
} from "@features/bx/bulk-bill-generation/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Progress } from "@shared/ui/progress";
import { navigateTo } from "@shared/utils/navigation";
import { ArrowDown, ArrowUp, Download, Eye } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const BillingSummary: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const navigateToStep = () => {
    navigateTo(
      navigate,
      "bx",
      "create-bill-generation",
      "approve",
      params?.id,
      "edit"
    );
  };

  const downloadBill = useDownloadBill();
  const viewBill = useViewBill();
  const CURRENCY = useCurrency();

  const handleDownloadClick = async (billId: string, filename?: string) => {
    try {
      await downloadBill.mutateAsync({
        billId,
        filename: filename || `bill-${billId}.pdf`,
      });
      // showToast('Bill downloaded successfully', 'success');
    } catch (error) {
      console.error("Download failed:", error);
      // showToast('Failed to download the bill', 'error');
    }
  };

  const handleViewClick = async (billId: string) => {
    try {
      await viewBill.mutateAsync(billId);
    } catch (error) {
      console.error("View failed:", error);
      // showToast('Failed to view the bill', 'error');
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("");
  const [selectedConsumers, setSelectedConsumers] = useState<string[]>([]);
  const [expandFinancial, setExpandFinancial] = useState(false);
  const [selectedService, setSelectedService] = useState<string>("all");

  interface Consumer {
    id: string;
    name: string;
    accountNo: string;
    serviceType: string;
    consumption: number;
    amount: number;
    outstanding: number;
  }

  const columns = [
    {
      key: "consumerName",
      header: "Consumer",
      render: (value: string) => <span className="font-medium">{value}</span>,
    },
    {
      key: "remoteConsumerNumber",
      header: "Account No",
    },
    {
      key: "serviceCharges",
      header: "Service",
    },
    {
      key: "consumption",
      header: "Consumption",
    },
    {
      key: "billAmount",
      header: "Bill Amount",
      render: (value: number) => (
        <span>
          {CURRENCY} {value}
        </span>
      ),
    },
    {
      key: "outstandingBalance",
      header: "Outstanding",
      render: (value: number) => <span>{`${CURRENCY} ${value ?? "NA"}`}</span>,
    },
    {
      key: "totalBilledAmount",
      header: "Total Due",
      render: (value: any, row: Consumer) => (
        <span>{`${CURRENCY} ${value ?? "NA"}`}</span>
      ),
    },
  ];
  interface Consumer {
    id: string;
    name: string;
    accountNo: string;
    serviceType: string;
    consumption: number;
    amount: number;
    outstanding: number;
  }
  const [allConsumers, setAllConsumers] = useState<Consumer[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const actions = [
    {
      label: <Eye className="h-4 w-4" />,
      onClick: (row: Consumer) => {
        handleViewClick(row.id);
      },
      icon: <Eye className="h-4 w-4" />,
    },
    {
      label: <Download className="h-4 w-4" />,
      onClick: (row: Consumer) => {
        handleDownloadClick(row.id);
      },
      icon: <Download className="h-4 w-4" />,
    },
  ];

  const [queryParams, setQueryParams] = useState<Record<string, any>>({
    page: currentPage,
    page_size: 50,
    search_data: searchTerm,
    product_code: serviceTypeFilter,
    bill_run_id: params?.id,
  });
  const { data: billData } = useBillData(queryParams);

  const { data: utilityService } = useUtilityServices();

  const {
    data: billGenerationSummary,
    isPending: billGenerationSummaryLoading,
    error: billGenerationSummaryError,
  } = useBillGenerationSummary(params?.id);

  const handleSearch = (searchTerm) => {
    setQueryParams((prev) => ({
      ...prev,
      search_data: searchTerm || undefined,
    }));
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-2xl font-bold text-primary">Billing Summary</h3>
          <p className="text-muted-foreground">
            Summary of the bills generated for the billing cycle
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Bills Generated</CardTitle>
            <CardDescription>
              {`Total bills successfully generated`} <br />
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-1">
              <div className="text-3xl font-bold flex items-end gap-2">
                {billGenerationSummary?.totalBills}
                <span className="text-xs font-normal text-success pb-1">
                  (
                  {billGenerationSummary?.billSuccessPercentFromPreviousRun ??
                    "NA"}
                  %)
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1 mb-1">
                This cycle •{" "}
                {billGenerationSummary?.billSuccessPercentage ?? "NA"}% of total
              </div>
              <div className="flex gap-2 text-xs items-center">
                Previous:
                <span className="font-bold">
                  {(billGenerationSummary?.previousActualBills ?? 0) +
                    (billGenerationSummary?.previousNonActualBills ?? 0)}
                </span>{" "}
                ({billGenerationSummary?.previousSuccessBillPercentage ?? "NA"}
                %)
              </div>
              <Progress
                value={
                  billGenerationSummary?.previousSuccessBillPercentage ?? "NA"
                }
                className="h-2 mt-2"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Bills Not Generated</CardTitle>
            <CardDescription>
              Number of bills that could not be generated
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-1">
              <div className="text-3xl font-bold flex items-end gap-2 text-destructive">
                {billGenerationSummary?.failedBills}
                <span className="text-xs font-normal pb-1">
                  ({billGenerationSummary?.currentFailedBillPercentage ?? "NA"}
                  %)
                </span>
              </div>
              <div className="flex gap-2 text-xs items-center text-muted-foreground">
                Previous:{}
                <span className="font-bold text-destructive">
                  {billGenerationSummary?.previousFailedBills ?? "NA"}
                </span>{" "}
                ({billGenerationSummary?.previousFailedBillPercentage ?? "NA"}
                %)
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Often due to missing readings/tariffs
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Bill Types</CardTitle>
            <CardDescription>Actual vs Estimated Readings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Actual Bills</span>
                  <span>{billGenerationSummary?.actualBills ?? "NA"}%</span>
                </div>
                <Progress
                  value={
                    billGenerationSummary?.actualBillComparisonPercent ?? "NA"
                  }
                  className="h-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>
                    This cycle: {billGenerationSummary?.actualBills ?? "NA"}
                  </span>
                  <span>
                    Last cycle:{" "}
                    {billGenerationSummary?.previousActualBills ?? "NA"}
                  </span>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Estimated Bills</span>
                  <span>
                    {billGenerationSummary?.nonActualBillComparisonPercent ??
                      "NA"}
                    %
                  </span>
                </div>
                <Progress
                  value={
                    billGenerationSummary?.nonActualBillComparisonPercent ??
                    "NA"
                  }
                  className="h-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>
                    This cycle: {billGenerationSummary?.nonActualBills}
                  </span>
                  <span>
                    Last cycle: {billGenerationSummary?.previousNonActualBills}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Consumption Distribution</CardTitle>
          <CardDescription>Breakdown of consumption patterns</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">
                Total Consumption
              </div>
              <div className="text-2xl font-bold">
                {billGenerationSummary?.totalConsumption ?? "NA"} unit
              </div>
              <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                <span>
                  Last:{" "}
                  {billGenerationSummary?.previousTotalConsumption ?? "NA"} unit
                </span>
                <span className="flex items-center">
                  {billGenerationSummary?.consumptionComparisonPercent ?? "NA"}%
                  from last cycle
                </span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">
                  High Consumption
                </div>
                <div className="text-2xl font-bold">
                  {billGenerationSummary?.highConsumptionBills}
                </div>
                <div className="text-xs text-muted-foreground">
                  This: {billGenerationSummary?.highConsumptionBills} • Last:{" "}
                  {billGenerationSummary?.previousHighConsumptionBills}
                </div>
                <div className="text-xs text-info">
                  {billGenerationSummary?.highConsumptionComparisonPercent}% vs
                  Last
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">
                  Low Consumption
                </div>
                <div className="text-2xl font-bold">
                  {billGenerationSummary?.lowConsumptionBills}
                </div>
                <div className="text-xs text-muted-foreground">
                  This: {billGenerationSummary?.lowConsumptionBills} • Last:{" "}
                  {billGenerationSummary?.previousLowConsumptionBills}
                </div>
                <div className="text-xs text-info">
                  {billGenerationSummary?.lowConsumptionComparisonPercent}% vs
                  Last
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">
                  Zero Consumption
                </div>
                <div className="text-2xl font-bold">
                  {billGenerationSummary?.zeroConsumptionBills}
                </div>
                <div className="text-xs text-muted-foreground">
                  This: {billGenerationSummary?.zeroConsumptionBills} • Last:{" "}
                  {billGenerationSummary?.previousZeroConsumptionBills}
                </div>
                <div className="text-xs text-info">
                  {billGenerationSummary?.zeroConsumptionComparisonPercent}% vs
                  Last
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Financial Summary</CardTitle>
          <CardDescription>
            Overview of billing financial metrics and late payments
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">
                Total Consumption
              </div>
              <div className="text-2xl font-bold">
                {billGenerationSummary?.totalConsumption} unit
              </div>
              <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                <span>
                  Last: {billGenerationSummary?.previousTotalConsumption} unit
                </span>
                <span className="flex items-center">
                  {parseFloat(
                    billGenerationSummary?.consumptionComparisonPercent
                  ) > 0 ? (
                    <ArrowUp className="h-3 w-3 text-success mr-1" />
                  ) : (
                    <ArrowDown className="h-3 w-3 text-destructive mr-1" />
                  )}
                  {billGenerationSummary?.consumptionComparisonPercent}% from
                  last cycle
                </span>
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">
                Total Billed Amount
              </div>
              <div className="text-2xl font-bold">
                {CURRENCY} {billGenerationSummary?.totalBillAmount}
              </div>
              <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                <span>
                  Last: {CURRENCY}{" "}
                  {billGenerationSummary?.previousTotalBillAmount}
                </span>
                <span className="flex items-center">
                  {parseFloat(
                    billGenerationSummary?.billAmountComparisonPercent
                  ) > 0 ? (
                    <ArrowUp className="h-3 w-3 text-success mr-1" />
                  ) : (
                    <ArrowDown className="h-3 w-3 text-destructive mr-1" />
                  )}
                  {billGenerationSummary?.billAmountComparisonPercent}% from
                  last cycle
                </span>
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">
                Service Charges
              </div>
              <div className="text-2xl font-bold">
                {CURRENCY} {billGenerationSummary?.totalServiceCharges}
              </div>
              <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                <span>
                  Last: {CURRENCY}{" "}
                  {billGenerationSummary?.previousTotalServiceCharges}
                </span>
                <span className="flex items-center">
                  {parseFloat(
                    billGenerationSummary?.percentageChangeInServiceCharges
                  ) > 0 ? (
                    <ArrowUp className="h-3 w-3 text-success mr-1" />
                  ) : (
                    <ArrowDown className="h-3 w-3 text-destructive mr-1" />
                  )}
                  {billGenerationSummary?.percentageChangeInServiceCharges}%
                  from last cycle
                </span>
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">
                Outstanding Amount
              </div>
              <div className="text-2xl font-bold">
                {CURRENCY} {billGenerationSummary?.totalOutstanding}
              </div>
              <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                <span>
                  Last: {CURRENCY}{" "}
                  {billGenerationSummary?.previousTotalOutstanding}
                </span>
                <span className="flex items-center">
                  {parseFloat(
                    billGenerationSummary?.outstandingComparisonPercent ?? "NA"
                  ) > 0 ? (
                    <ArrowUp className="h-3 w-3 text-destructive mr-1" />
                  ) : (
                    <ArrowDown className="h-3 w-3 text-success mr-1" />
                  )}
                  {billGenerationSummary?.outstandingComparisonPercent ?? "NA"}%
                  from last cycle
                </span>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t">
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Late Payments</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-2xl font-bold flex items-center gap-2">
                    {billGenerationSummary?.currentLatePaymentConsumer ?? "NA"}{" "}
                    <span className="text-sm text-muted-foreground">bills</span>
                  </div>
                  <div className="text-sm">
                    Late Amount:
                    <span className="font-bold text-warning">
                      {" "}
                      {CURRENCY} {billGenerationSummary?.latePayment}
                    </span>
                  </div>
                  <div className="flex gap-2 text-xs items-center text-muted-foreground mt-1">
                    Previous:{" "}
                    <strong>
                      {billGenerationSummary?.previousLatePayment ?? "NA"}
                    </strong>
                    bills,{" "}
                    <span className="font-semibold">
                      {CURRENCY}{" "}
                      {billGenerationSummary?.previousLatePaymentConsumer ??
                        "NA"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Consumer Bills</CardTitle>
          <CardDescription>
            List of all consumers with bill details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto max-h-[500px] mt-5">
            <GenericTable
              data={billData?.result ?? []}
              columns={columns}
              rowKey="id"
              actions={actions}
              actionsType="buttons"
              getRowClassName={(row) => ""} // Add any custom row styling here
              emptyMessage="No consumers found"
              searchPlaceholder="Search by account number"
              searchParamName="search_data"
              onSearch={handleSearch}
              pagination={{
                currentPage,
                totalPages: Math.ceil((billData?.count || 0) / itemsPerPage),
                totalItems: billData?.count || 0,
                itemsPerPage: itemsPerPage,
                startItem: (currentPage - 1) * itemsPerPage + 1,
                endItem: Math.min(
                  currentPage * itemsPerPage,
                  billData?.count || 0
                ),
              }}
              onPageChange={(e) => {
                setCurrentPage(e);
                setQueryParams((prev) => ({
                  ...prev,
                  page: e || undefined,
                }));
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingSummary;
